import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { portalApi } from '../lib/api';
import { applyTenantBranding } from '../theme/tokens';
import { money, date, invoiceTotal } from '../shared/formatters';
import StatusPill from '../shared/components/StatusPill';
import Skeleton from '../shared/components/Skeleton';
import { useToast } from '../shared/components/ToastProvider';
import SafeIcon from '../common/SafeIcon';
import { Icons } from '../shared/icons';

const DOC_TYPE_LABELS = {
  invoice: 'Invoice',
  estimate: 'Estimate',
  quote: 'Quotation',
};

// Public invoice view — unauthenticated, branded, read-only.
// Route: /i/:token (public block in routes.jsx, outside auth gates).
// Fetches from backend contract endpoint 1, applies tenant branding,
// shows Pay now button via Stripe Checkout (contract endpoint 2).
export default function PublicInvoiceView() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const { notify } = useToast();
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [client, setClient] = useState(null);
  const [tenant, setTenant] = useState(null);

  const paid = searchParams.get('paid') === '1';
  const cancelled = searchParams.get('cancelled') === '1';

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const result = await portalApi.publicInvoice(token);
        if (active) {
          setInvoice(result.invoice);
          setClient(result.client);
          setTenant(result.tenant);
          applyTenantBranding({
            primary: result.tenant.primaryColor,
            primaryStrong: result.tenant.primaryColor,
            accent: result.tenant.accentColor,
          });
        }
      } catch (err) {
        if (active) {
          if (err.message?.includes('404') || err.message?.toLowerCase().includes('not found')) {
            notify('Invoice not found or link has expired', 'error');
          } else {
            notify(err.message || 'Failed to load invoice', 'error');
          }
        }
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, [token, notify]);

  const handlePayNow = async () => {
    setPaying(true);
    try {
      const result = await portalApi.createPublicCheckoutSession(token);
      window.location = result.url;
    } catch (err) {
      if (err.message?.includes('409') || err.message?.toLowerCase().includes('already paid')) {
        notify('This invoice has already been paid', 'error');
        // Refetch to get updated status
        const result = await portalApi.publicInvoice(token);
        setInvoice(result.invoice);
      } else if (err.message?.includes('400') || err.message?.toLowerCase().includes('not configured')) {
        notify('Card payments are not configured', 'error');
      } else if (err.message?.includes('429')) {
        notify('Too many payment attempts — please try again later', 'error');
      } else {
        notify(err.message || 'Could not start payment', 'error');
      }
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="public-invoice-page">
        <Skeleton rows={6} />
      </div>
    );
  }

  if (!invoice || !client || !tenant) {
    return (
      <div className="public-invoice-page">
        <div className="portal-card">
          <div className="empty-state">
            <SafeIcon icon={Icons.FileText} size={48} />
            <h2>Invoice not found</h2>
            <p>This link may have expired or been removed.</p>
          </div>
        </div>
      </div>
    );
  }

  const docLabel = DOC_TYPE_LABELS[invoice.docType] || 'Invoice';
  const isInvoice = invoice.docType === 'invoice';
  const canPay = isInvoice && (invoice.status === 'sent' || invoice.status === 'overdue');
  const showPayButton = canPay && invoice.status !== 'paid';

  return (
    <div className="public-invoice-page">
      {paid && (
        <div className="public-banner public-banner-success">
          <SafeIcon icon={Icons.CheckCircle} />
          Payment received — thank you
        </div>
      )}
      {cancelled && !paid && (
        <div className="public-banner public-banner-neutral">
          Payment cancelled — you can try again anytime
        </div>
      )}

      <div className="portal-card public-invoice-doc">
        {/* Branded header band */}
        <div className="brand-band">
          <div className="brand-band-content">
            <strong>{tenant.name}</strong>
            <StatusPill status={invoice.status} />
          </div>
        </div>

        {/* Document header */}
        <div className="document-header">
          <div>
            <span className="eyebrow">{docLabel}</span>
            <h1>{invoice.number}</h1>
          </div>
        </div>

        {/* Bill-to and dates */}
        <div className="invoice-meta">
          <div className="bill-to">
            <span className="meta-label">Billed to</span>
            <strong>{client.name}</strong>
          </div>
          <div className="invoice-dates">
            {invoice.issuedAt && (
              <div>
                <span className="meta-label">Issued</span>
                <span>{date(invoice.issuedAt)}</span>
              </div>
            )}
            {invoice.dueDate && (
              <div>
                <span className="meta-label">Due</span>
                <span>{date(invoice.dueDate)}</span>
              </div>
            )}
            {invoice.paidDate && invoice.status === 'paid' && (
              <div>
                <span className="meta-label">Paid</span>
                <span>{date(invoice.paidDate)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Line items table */}
        <table className="line-items-table">
          <thead>
            <tr>
              <th>Description</th>
              <th className="qty-col">Qty</th>
              <th className="price-col">Price</th>
              <th className="total-col">Total</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.lineItems || []).map((item, index) => (
              <tr key={`${item.description}-${index}`}>
                <td className="desc-col">{item.description || '—'}</td>
                <td className="qty-col">{item.qty || 0}</td>
                <td className="price-col">{money(item.unitPrice)}</td>
                <td className="total-col">{money((item.qty || 0) * (item.unitPrice || 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Grand total */}
        <div className="invoice-total">
          <span>Total</span>
          <strong>{money(invoice.total)}</strong>
        </div>

        {/* Payment instructions */}
        {tenant.paymentInstructions && (
          <div className="payment-instructions">
            <span className="meta-label">Payment instructions</span>
            <p>{tenant.paymentInstructions}</p>
          </div>
        )}

        {/* Pay now button */}
        {showPayButton && (
          <div className="public-invoice-actions">
            <button
              className="primary-button"
              onClick={handlePayNow}
              disabled={paying}
            >
              {paying ? 'Opening payment…' : 'Pay now'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}